/**
 * The inquiry form's data: its starting values, the checks run before
 * submitting, and the shape sent to POST /api/meet-assist-inquiry.
 *
 * One form object is shared by every tab, so a guest who switches from
 * Arrival to Departure keeps their passenger and contact details.
 */
import { formatPayloadDate } from "./dates";
import {
  CAR_DETAILS_READY,
  CONTACT_DIFFERENT,
  CONTACT_SAME,
  CAR_DETAILS_LATER,
  DEFAULT_LOCATION,
  DEFAULT_PHONE,
  GROUP_AIRPORT,
  GROUP_STATION,
  MIN_ADULTS,
  PASSENGER_TITLE_OPTIONS,
  PICKUP_SIGN_CUSTOM,
  PICKUP_SIGN_SAME,
  SCHEDULE_FIELD_TYPES,
  SERVICE_CONFIG,
  findLocation,
} from "./constants";

// Error messages are translation keys, resolved by the field that shows them.
export const ERROR_REQUIRED = "Errors.Required";
export const ERROR_INVALID_EMAIL = "Errors.InvalidEmail";

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const createInitialForm = () => ({
  locations: {
    [GROUP_AIRPORT]: { ...DEFAULT_LOCATION[GROUP_AIRPORT] },
    [GROUP_STATION]: { ...DEFAULT_LOCATION[GROUP_STATION] },
  },
  // Remembered per service, because each one offers different tiers.
  tierByService: {},
  adults: MIN_ADULTS,
  children: 0,
  // One entry per child; "" until the guest picks an age.
  childAges: [],
  trainDetails: "",
  arrivalFlightNumber: "",
  arrivalDate: null,
  arrivalTime: "",
  departureFlightNumber: "",
  departureDate: null,
  departureTime: "",
  meetingTime: "",
  priorLocation: "",
  timeLeaving: "",
  specialRequests: "",
  leadTitle: PASSENGER_TITLE_OPTIONS[0],
  leadName: "",
  pickupSignType: PICKUP_SIGN_SAME,
  pickupSignText: "",
  email: "",
  phone: { ...DEFAULT_PHONE },
  carDetailsType: CAR_DETAILS_READY,
  carPlateNumber: "",
  driverName: "",
  driverPhone: { ...DEFAULT_PHONE },
  contactType: CONTACT_SAME,
  contactTitle: PASSENGER_TITLE_OPTIONS[0],
  contactName: "",
  contactEmail: "",
  contactPhone: { ...DEFAULT_PHONE },
});

/** The tier in use for a service: the guest's pick, else the first one offered. */
export const getSelectedTier = (form, serviceKey) =>
  form.tierByService[serviceKey] || SERVICE_CONFIG[serviceKey].tiers[0];

export const getPassengerCount = (form) => form.adults + form.children;

const isBlank = (value) => value === null || value === undefined || String(value).trim() === "";

/** Every schedule field the current service shows, e.g. flight number, date, time. */
const getScheduleFieldNames = (serviceConfig) => {
  const fieldNames = serviceConfig.scheduleRows.flat();
  if (serviceConfig.hasPriorLocation) fieldNames.push("priorLocation", "timeLeaving");
  return fieldNames;
};

const validateEmailField = (errors, fieldName, value) => {
  if (isBlank(value)) errors[fieldName] = ERROR_REQUIRED;
  else if (!EMAIL_PATTERN.test(value.trim())) errors[fieldName] = ERROR_INVALID_EMAIL;
};

/**
 * Checks the form for the service being shown.
 *
 * @param {Object} form
 * @param {string} serviceKey
 * @returns {Object} `{ fieldName: translationKey }`; empty when the form is ready
 */
export const validateInquiryForm = (form, serviceKey) => {
  const serviceConfig = SERVICE_CONFIG[serviceKey];
  const errors = {};

  const location = form.locations[serviceConfig.group];
  if (isBlank(location.placeId)) errors.locationName = ERROR_REQUIRED;
  if (serviceConfig.group === GROUP_AIRPORT && isBlank(location.terminal)) errors.terminalName = ERROR_REQUIRED;

  getScheduleFieldNames(serviceConfig).forEach((fieldName) => {
    if (isBlank(form[fieldName])) errors[fieldName] = ERROR_REQUIRED;
  });

  form.childAges.forEach((age, childIndex) => {
    if (isBlank(age)) errors[`childAge${childIndex}`] = ERROR_REQUIRED;
  });

  if (isBlank(form.leadName)) errors.leadName = ERROR_REQUIRED;
  if (form.pickupSignType === PICKUP_SIGN_CUSTOM && isBlank(form.pickupSignText)) {
    errors.pickupSignText = ERROR_REQUIRED;
  }
  validateEmailField(errors, "email", form.email);
  if (isBlank(form.phone.number)) errors.phone = ERROR_REQUIRED;

  if (serviceConfig.hasCarAndDriver && form.carDetailsType === CAR_DETAILS_READY) {
    if (isBlank(form.carPlateNumber)) errors.carPlateNumber = ERROR_REQUIRED;
    if (isBlank(form.driverName)) errors.driverName = ERROR_REQUIRED;
    if (isBlank(form.driverPhone.number)) errors.driverPhone = ERROR_REQUIRED;
  }

  if (form.contactType === CONTACT_DIFFERENT) {
    if (isBlank(form.contactName)) errors.contactName = ERROR_REQUIRED;
    validateEmailField(errors, "contactEmail", form.contactEmail);
    if (isBlank(form.contactPhone.number)) errors.contactPhone = ERROR_REQUIRED;
  }

  return errors;
};

/**
 * Server field names that differ from the form's, so a 400 from the server
 * can highlight the right input.
 */
const SERVER_TO_FORM_FIELD = {
  "leadPassenger.name": "leadName",
  "pickupSign.customText": "pickupSignText",
  pickupSign: "pickupSignText",
  "phone.number": "phone",
  "phone.dialCode": "phone",
  carPlateNumber: "carPlateNumber",
  "carDriver.plateNumber": "carPlateNumber",
  "carDriver.driverName": "driverName",
  driverName: "driverName",
  driverPhone: "driverPhone",
  "contact.name": "contactName",
  contactName: "contactName",
  "contact.email": "contactEmail",
  contactEmail: "contactEmail",
  contactPhone: "contactPhone",
};

/** Turns the server's `fieldErrors` into the form's error map. */
export const mapServerFieldErrors = (serverFieldErrors = {}) =>
  Object.keys(serverFieldErrors).reduce((formErrors, serverFieldName) => {
    formErrors[SERVER_TO_FORM_FIELD[serverFieldName] || serverFieldName] = ERROR_REQUIRED;
    return formErrors;
  }, {});

const toPayloadPhone = (phone) => ({ dialCode: phone.dialCode, number: phone.number.trim() });

/** Only the fields the current service shows are sent; the rest stay empty. */
const buildSchedulePayload = (form, serviceConfig) =>
  getScheduleFieldNames(serviceConfig).reduce((schedule, fieldName) => {
    const isDateField = SCHEDULE_FIELD_TYPES[fieldName] === "date";
    schedule[fieldName] = isDateField ? formatPayloadDate(form[fieldName]) : String(form[fieldName]).trim();
    return schedule;
  }, {});

/**
 * Builds the request body. Location and terminal are sent as English labels
 * because the admin email is written in English.
 *
 * @param {Object}   args
 * @param {Object}   args.form
 * @param {string}   args.serviceKey
 * @param {number}   args.estimatedTotal - the total the guest saw
 * @param {string}   args.language       - "en" or "jp"
 * @param {Function} args.translateToEnglish - i18n `t` fixed to English
 */
export const buildInquiryPayload = ({ form, serviceKey, estimatedTotal, language, translateToEnglish }) => {
  const serviceConfig = SERVICE_CONFIG[serviceKey];
  const location = form.locations[serviceConfig.group];
  const locationDefinition = findLocation(serviceConfig.group, location.placeId);
  const isAirport = serviceConfig.group === GROUP_AIRPORT;
  const childAges = form.childAges.map(Number);

  return {
    serviceKey,
    tier: getSelectedTier(form, serviceKey),
    adults: form.adults,
    children: form.children,
    childAges,
    locationName: locationDefinition ? translateToEnglish(locationDefinition.labelKey) : "",
    terminalName: isAirport && location.terminal ? translateToEnglish(`Terminals.${location.terminal}`) : "",
    trainDetails: isAirport ? "" : form.trainDetails.trim(),
    ...buildSchedulePayload(form, serviceConfig),
    specialRequests: form.specialRequests.trim(),
    leadPassenger: { title: form.leadTitle, name: form.leadName.trim() },
    pickupSign: {
      type: form.pickupSignType,
      customText: form.pickupSignType === PICKUP_SIGN_CUSTOM ? form.pickupSignText.trim() : "",
    },
    email: form.email.trim(),
    phone: toPayloadPhone(form.phone),
    carDriver: serviceConfig.hasCarAndDriver
      ? {
          type: form.carDetailsType,
          plateNumber: form.carDetailsType === CAR_DETAILS_READY ? form.carPlateNumber.trim() : "",
          driverName: form.carDetailsType === CAR_DETAILS_READY ? form.driverName.trim() : "",
          driverPhone: toPayloadPhone(form.carDetailsType === CAR_DETAILS_READY ? form.driverPhone : DEFAULT_PHONE),
        }
      : { type: CAR_DETAILS_LATER },
    contact:
      form.contactType === CONTACT_DIFFERENT
        ? {
            type: CONTACT_DIFFERENT,
            title: form.contactTitle,
            name: form.contactName.trim(),
            email: form.contactEmail.trim(),
            phone: toPayloadPhone(form.contactPhone),
          }
        : { type: CONTACT_SAME },
    estimatedTotal,
    language,
  };
};
